import { useState } from "react";
import { getAbsoluteApiUrl } from "@/lib/auth-utils";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Copy, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CardContent } from "@mui/material";
import { refreshOrder } from "@/api/order-api";

interface QRInfoCardProps {
  orderId: string;
  onRefesh: () => void;
}

export default function QRInfoCard({ orderId, onRefesh }: QRInfoCardProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  // Cache-buster state: changing it forces the <img> to reload
  const [cacheBuster, setCacheBuster] = useState(() => crypto.randomUUID());

  // Always an absolute URL — safe for <img src> and clipboard in all envs
  const qrUrl = getAbsoluteApiUrl(`qr/${orderId}`);
  const qrImgSrc = `${qrUrl}?cb=${cacheBuster}`;

  const handleRefresh = async () => {
    const resp = await refreshOrder(orderId);
    if (resp.success) {
      // Bump cache-buster so the <img> re-fetches the new QR
      setCacheBuster(crypto.randomUUID());
      toast({
        title: "Success",
        description: "QR has been refreshed.",
        duration: 2000,
      });
      onRefesh();
    } else {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to refresh QR.",
      });
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      toast({
        title: "បានចម្លងជោគជ័យ!",
        description: "Link has been copied to clipboard.",
        duration: 2000,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to copy link.",
      });
    }
  };

  return (
    <Card className="border-slate-100 rounded-3xl overflow-hidden bg-slate-50 w-full">
      <CardContent className="px-4 py-4 md:px-4 md:py-4">
        <div className="flex flex-col items-center">

          {/* QR image */}
          <div className="bg-slate-50 p-4 md:p-5 rounded-[2rem] border-2 border-dashed border-slate-200 transition-colors hover:border-blue-300 group flex items-center justify-center w-full">
            <img
              src={qrImgSrc}
              alt="Payment QR"
              className="w-full max-w-[200px] md:max-w-[220px] h-auto rounded-xl shadow-sm group-hover:scale-105 transition-transform duration-300"
            />
          </div>

          <div className="w-full space-y-3 md:space-y-4 mt-4 md:mt-6">
            <Button
              onClick={handleRefresh}
              className="w-full h-10 text-base md:text-lg font-black gap-3 rounded-2xl shadow-lg
              transition-all duration-200 active:scale-95
              bg-blue-600 hover:bg-blue-700"
            >
              Refresh QR
            </Button>

            <Button
              onClick={handleCopy}
              className={`
                w-full h-10 text-base md:text-lg font-black gap-3 rounded-2xl shadow-lg
                transition-all duration-200 active:scale-95
                ${copied ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}
              `}
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4 animate-in zoom-in" />
                  ចម្លងរួចរាល់ / Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  ចម្លង / Copy
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
